/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, CallEffect, takeLatest, put, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  Asset,
  CollectionApi,
  Constants,
  Ducks,
  FileFormat,
  MediaFileCollection,
  MediaFileSubscription,
  SubscriptionsApi,
  Time,
  TimeUtils,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';

export enum ActionTypes {
  COLLECTIONS_FOR_MEDIA_FILES_REQUEST = 'collectionsForMediaFiles/REQUEST',
  SUBSCRIPTIONS_FOR_MEDIA_FILES_REQUEST = 'subscriptionsForMediaFiles/REQUEST',
  SUBSCRIPTIONS_FOR_MEDIA_FILES_SUCCESS = 'subscriptionsForMediaFiles/SUCCESS',
  SUBSCRIPTIONS_FOR_MEDIA_FILES_FAIL = 'subscriptionsForMediaFiles/FAIL',
  COLLECTIONS_FOR_MEDIA_FILES_SUCCESS = 'collectionsForMediaFiles/SUCCESS',
  COLLECTIONS_FOR_MEDIA_FILES_FAIL = 'collectionsForMediaFiles/FAIL',
}

interface Product {
  productId: string;
  mediaFileId: number;
}

interface ProductsCache {
  productsIds?: string[];
  fetched?: Time;
  expires?: Time;
}

interface RequestSuccessPayload {
  products: Product[];
  fetched?: Time;
  expires?: Time;
}

interface MetaRequest {
  asset: Asset;
}

type SubscriptionsForMediaFilesRequest = ActionRequest<ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_REQUEST, MetaRequest>;
type CollectionsForMediaFilesRequest = ActionRequest<ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_REQUEST, MetaRequest>;
export type CollectionsForMediaFilesRequestFail = ActionFail<ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_FAIL, MetaRequest>;
export type SubscriptionsForMediaFilesRequestFail = ActionFail<
  ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_FAIL,
  MetaRequest
>;
export type SubscriptionsForMediaFilesSuccess = ActionSuccess<
  ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_SUCCESS,
  RequestSuccessPayload,
  MetaRequest
>;
export type CollectionsForMediaFilesSuccess = ActionSuccess<
  ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_SUCCESS,
  RequestSuccessPayload,
  MetaRequest
>;

type Actions =
  | SubscriptionsForMediaFilesRequest
  | CollectionsForMediaFilesRequest
  | SubscriptionsForMediaFilesSuccess
  | CollectionsForMediaFilesSuccess
  | SubscriptionsForMediaFilesSuccess
  | SubscriptionsForMediaFilesRequestFail
  | CollectionsForMediaFilesRequestFail;

const Actions = {
  subscriptionsForMediaFilesRequest: (meta: MetaRequest): SubscriptionsForMediaFilesRequest => ({
    type: ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_REQUEST,
    meta,
  }),
  collectionsForMediaFilesRequest: (meta: MetaRequest): CollectionsForMediaFilesRequest => ({
    type: ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_REQUEST,
    meta,
  }),
  subscriptionsForMediaFilesSuccess: (
    payload: RequestSuccessPayload,
    meta: MetaRequest
  ): SubscriptionsForMediaFilesSuccess => ({
    type: ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_SUCCESS,
    payload,
    meta,
  }),
  collectionsForMediaFilesSuccess: (
    payload: RequestSuccessPayload,
    meta: MetaRequest
  ): CollectionsForMediaFilesSuccess => ({
    type: ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_SUCCESS,
    payload,
    meta,
  }),
  collectionsForMediaFilesFail: (error: XError, meta: MetaRequest): CollectionsForMediaFilesRequestFail => ({
    type: ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_FAIL,
    error,
    meta,
  }),
  subscriptionsForMediaFilesFail: (error: XError, meta: MetaRequest): SubscriptionsForMediaFilesRequestFail => ({
    type: ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_FAIL,
    error,
    meta,
  }),
};

export const ActionCreators = {
  subscriptionsForMediaFilesRequest: Actions.subscriptionsForMediaFilesRequest,
  collectionsForMediaFilesRequest: Actions.collectionsForMediaFilesRequest,
};

export interface State {
  collection?: ProductsCache;
  subscription?: ProductsCache;
}

export interface ReducerState {
  [mediaFileId: number]: State;
}

const initialState: ReducerState = {};

export default function vodProductIdCache(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_REQUEST:
    case ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_REQUEST:
    case ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_FAIL:
    case ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_FAIL:
      return state;
    case ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_SUCCESS: {
      const { meta, payload } = action;
      let newStates: ReducerState = {};
      payload.products.reduce((acc: number[], cur) => {
        if (!acc.find((mediaFileId) => mediaFileId === cur.mediaFileId)) {
          const productsIdsByMediaFileIds = payload.products
            .filter((prod) => prod.mediaFileId === cur.mediaFileId)
            .map((p) => p.productId);
          const newSubscriptionStateByMediaFile: ReducerState = {
            [cur.mediaFileId]: {
              subscription: {
                productsIds: productsIdsByMediaFileIds,
                fetched: payload.fetched,
                expires: payload.expires,
              },
              collection: state[meta.asset.id]?.collection,
            },
          };
          newStates = { ...newStates, ...newSubscriptionStateByMediaFile };
          acc.push(cur.mediaFileId);
        }
        return acc;
      }, []);

      return {
        ...state,
        ...newStates,
      };
    }
    case ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_SUCCESS: {
      const { meta, payload } = action;
      let newStates: ReducerState = {};
      payload.products.reduce((acc: number[], cur) => {
        if (!acc.find((mediaFileId) => mediaFileId === cur.mediaFileId)) {
          const productsIdsByMediaFileIds = payload.products
            .filter((prod) => prod.mediaFileId === cur.mediaFileId)
            .map((p) => p.productId);
          const newCollectionStateByMediaFile: ReducerState = {
            [cur.mediaFileId]: {
              collection: {
                productsIds: productsIdsByMediaFileIds,
                fetched: payload.fetched,
                expires: payload.expires,
              },
              subscription: state[meta.asset.id]?.subscription,
            },
          };
          newStates = { ...newStates, ...newCollectionStateByMediaFile };
          acc.push(cur.mediaFileId);
        }
        return acc;
      }, []);

      return {
        ...state,
        ...newStates,
      };
    }
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getVodProductIdByMediaFileId: (state: StoreState, mediaFileId: number): State | undefined => {
    return state.vodProductIdCache[mediaFileId];
  },
  getVodProductIdByMediaFileIds: (state: StoreState, mediaFileIds: number[]): State[] => {
    const stateToReturn: State[] = [];
    mediaFileIds.forEach((mediaFile) => {
      const vodProductId = state.vodProductIdCache[mediaFile];
      if (vodProductId) {
        stateToReturn.push(vodProductId);
      }
    });
    return stateToReturn;
  },
};

function* subscriptionsForMediaFilesRequestSaga(action: SubscriptionsForMediaFilesRequest): SagaIterator {
  const { meta } = action;
  const { asset } = meta;
  const tasks: CallEffect[] = [];

  const mediaFileIds = asset.streams.filter((stream) => stream.fileFormat !== FileFormat.TRAILER).map((s) => s.id);
  const vodProductIdsCache: State[] = yield select(Selectors.getVodProductIdByMediaFileIds, mediaFileIds);
  const vodProductIdsCacheValid = vodProductIdsCache.filter((vodProducId) => {
    return (
      (vodProducId.subscription?.expires && TimeUtils.isAfterNow(vodProducId.subscription.expires)) ||
      (vodProducId.collection?.expires && TimeUtils.isAfterNow(vodProducId.collection.expires))
    );
  });

  asset.streams
    .filter((stream) => stream.fileFormat !== FileFormat.TRAILER)
    .reduce((acc: number[], cur) => {
      if (!acc.find((mediaFileId) => mediaFileId === cur.id) && !vodProductIdsCacheValid.find((id) => id === cur.id)) {
        acc.push(cur.id);
        tasks.push(Ducks.Api.callApi(SubscriptionsApi.getSubscriptionsMediaFile, cur.id));
      }
      return acc;
    }, []);

  const TasksResult: [MediaFileSubscription[]] = yield all(tasks);

  if (!TasksResult.length) {
    yield put(
      Actions.subscriptionsForMediaFilesFail(
        XError.fromError(
          new Error('Error on subscriptions request by media file: The media files are unavailable to request.')
        ),
        meta
      )
    );
  }

  const subscriptionsMediaFiles = TasksResult.reduce((acc, cur) => {
    acc.push(...cur);
    return acc;
  }, []);

  const payload: RequestSuccessPayload = {
    products: subscriptionsMediaFiles.map((subscription) => {
      return {
        productId: subscription.productId,
        mediaFileId: subscription.mediaFileId,
      };
    }),
    fetched: TimeUtils.now(),
    expires: TimeUtils.addMinutes(TimeUtils.now(), Constants.VOD_PRODUCT_ID_CACHE_TIME),
  };

  yield put(Actions.subscriptionsForMediaFilesSuccess(payload, meta));
}

function* collectionsForMediaFilesRequestSaga(action: CollectionsForMediaFilesRequest): SagaIterator {
  const tasks: CallEffect[] = [];
  const { meta } = action;
  const { asset } = meta;

  const mediaFileIds = asset.streams.filter((stream) => stream.fileFormat !== FileFormat.TRAILER).map((s) => s.id);
  const vodProductIdsCache: State[] = yield select(Selectors.getVodProductIdByMediaFileIds, mediaFileIds);
  const vodProductIdsCacheValid = vodProductIdsCache.filter((vodProducId) => {
    return (
      (vodProducId.subscription?.expires && TimeUtils.isAfterNow(vodProducId.subscription.expires)) ||
      (vodProducId.collection?.expires && TimeUtils.isAfterNow(vodProducId.collection.expires))
    );
  });

  asset.streams
    .filter((stream) => stream.fileFormat !== FileFormat.TRAILER)
    .reduce((acc: number[], cur) => {
      if (!acc.find((mediaFileId) => mediaFileId === cur.id) && !vodProductIdsCacheValid.find((id) => id === cur.id)) {
        acc.push(cur.id);
        tasks.push(Ducks.Api.callApi(CollectionApi.getCollectionsMediaFile, cur.id));
      }
      return acc;
    }, []);

  const TasksResult: [MediaFileCollection[]] = yield all(tasks);

  if (!TasksResult.length) {
    yield put(
      Actions.collectionsForMediaFilesFail(
        XError.fromError(
          new Error('Error on collections request by media file: The media files are unavailable to request.')
        ),
        meta
      )
    );
  }

  const collectionsMediaFiles = TasksResult.reduce((acc, cur) => {
    acc.push(...cur);
    return acc;
  }, []);

  const payload: RequestSuccessPayload = {
    products: collectionsMediaFiles.map((collection) => {
      return {
        productId: collection.productId,
        mediaFileId: collection.mediaFileId,
      };
    }),
    fetched: TimeUtils.now(),
    expires: TimeUtils.addMinutes(TimeUtils.now(), Constants.VOD_PRODUCT_ID_CACHE_TIME),
  };

  yield put(Actions.collectionsForMediaFilesSuccess(payload, meta));
}

export function* saga(): SagaIterator {
  yield all([
    takeLatest(ActionTypes.COLLECTIONS_FOR_MEDIA_FILES_REQUEST, collectionsForMediaFilesRequestSaga),
    takeLatest(ActionTypes.SUBSCRIPTIONS_FOR_MEDIA_FILES_REQUEST, subscriptionsForMediaFilesRequestSaga),
  ]);
}
